import type {
  StorageObjectGroupListV2ApiData,
  StorageObjectGroupListV2ToExcelApiData,
  StorageObjectGroupRecordListV2
} from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import type { ApiInput } from 'noddi-async';
import { invalidateQuery, noddiAsync, URLKeys } from 'noddi-async';
import { StorageObjectGroupTypeValues } from 'noddi-types';
import {
  ApiErrorPageInternal,
  filterUrlState,
  NoddiBadge,
  NoddiButton,
  NoddiTable,
  noddiToast,
  RowSelectionAction,
  type TableAction,
  useFilterUnits,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { partnerRoutes } from 'noddi-util';
import { useState } from 'react';
import {
  storageObjectGroupFilterUnit,
  useStorageObjectGroupTableFilters
} from '../../hooks/filters/TireHotel/useStorageObjectGroupTableFilter';
import { AppType, getExternalAppUrl } from '../../utils/navigation';
import { copyLicensePlateAction } from '../CarDetails/copyLicensePlateAction';
import { wheelSetColumns } from './columns';
import { LicensePlateBulkFilterDialog } from './LicensePlateBulkFilterDialog';
import { MoveStorageObjectsDialog } from './MoveStorageObjectsDialog';
import { useWheelSetLabelActions } from './useWheelSetLabelActions';

type Props = {
  userGroupIds?: number[];
  defaultStorageFacilityId?: number;
  /** Storage facility whose labels the print actions are for. Omitted: its labels are not printed, so both are hidden. */
  printListStorageFacilityId?: number;
  onSuccess?: () => void;
  isPartnerApp?: boolean;
} & (
  | { serviceOrganizationId: number; serviceDepartmentId?: undefined }
  | { serviceOrganizationId?: undefined; serviceDepartmentId: number }
);

export const WheelSets = ({
  serviceDepartmentId,
  serviceOrganizationId,
  userGroupIds,
  defaultStorageFacilityId,
  printListStorageFacilityId,
  isPartnerApp,
  onSuccess
}: Props) => {
  const { pagination, setPagination } = usePaginationQueryParam();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [identifiersToMove, setIdentifiersToMove] = useState<string[] | null>(null);
  const [isLicensePlateDialogOpen, setIsLicensePlateDialogOpen] = useState(false);
  const [isSelectAllMatchingPending, setIsSelectAllMatchingPending] = useState(false);
  const labelActions = useWheelSetLabelActions({
    printListStorageFacilityId,
    rowSelection,
    clearSelection: () => setRowSelection({})
  });

  const plates = useFilterUnits([licensePlatesFilter]);
  const licensePlateNumbers = plates.apiInput.licensePlateNumbers ?? [];
  const setLicensePlateNumbers = (plateNumbers: string[]) => {
    // A compose spec's control is typed only as `FilterControl`.
    const control = plates.controlOf(licensePlatesFilter);
    if (control?.type === 'multi-select') {
      control.onChange({ include: plateNumbers, exclude: [] });
    }
  };

  const storageObjectGroupFilters = useStorageObjectGroupTableFilters({
    serviceDepartmentIds: serviceDepartmentId ? [serviceDepartmentId] : undefined
  });
  // The plates are set from a dialog outside the filter sidebar, which previews candidate plates before applying them.
  const filters = useFilterUnits([
    storageObjectGroupFilterUnit.locked({
      ...(serviceDepartmentId
        ? { serviceDepartmentIds: [serviceDepartmentId] }
        : { serviceOrganizationIds: [serviceOrganizationId!] }),
      types: [StorageObjectGroupTypeValues.CAR_WHEELS],
      userGroupIds,
      hasStorageHistory: true
    }),
    storageObjectGroupFilters,
    storageObjectGroupFilterUnit.locked({ licensePlateNumbers: plates.apiInput.licensePlateNumbers })
  ]);

  const buildFilterInput = (
    plateNumbers: string[]
  ): ApiInput<StorageObjectGroupListV2ApiData> & ApiInput<StorageObjectGroupListV2ToExcelApiData> => ({
    search: pagination.search,
    ...filters.apiInput,
    licensePlateNumbers: plateNumbers.length > 0 ? plateNumbers : undefined
  });

  const { mutateAsync: downloadWheelSetsExcel, isPending: isExportPending } = noddiAsync.useDownload({
    type: URLKeys.getStorageObjectGroupListV2ToExcel
  });

  const {
    data: storageObjectGroups,
    isPending: isStorageObjectGroupsPending,
    isFetching: isStorageObjectGroupsFetching,
    error: storageObjectGroupsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedStorageObjectGroupsV2,
    input: {
      ...buildFilterInput(licensePlateNumbers),
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  const handleSuccess = () => {
    onSuccess?.();
    invalidateQuery({ urlKey: URLKeys.getPaginatedStorageObjectGroupsV2 });
    setRowSelection((selection) =>
      Object.fromEntries(Object.entries(selection).filter(([id]) => !identifiersToMove?.includes(id)))
    );
  };

  const handleSelectAllMatchingWheelSets = async () => {
    if (!storageObjectGroups?.count) {
      return;
    }

    setIsSelectAllMatchingPending(true);
    try {
      const response = await noddiAsync.downloadFile({
        type: URLKeys.getPaginatedStorageObjectGroupsV2,
        input: buildFilterInput(licensePlateNumbers)
      });

      labelActions.rememberSelectableRows(response.results);
      setRowSelection(Object.fromEntries(response.results.map((row) => [row.identifier, true])));
    } catch {
      noddiToast.error('Could not load all wheel sets for selection. Try again.');
    } finally {
      setIsSelectAllMatchingPending(false);
    }
  };

  if (storageObjectGroupsError) {
    return <ApiErrorPageInternal error={[storageObjectGroupsError]} />;
  }

  const rowSelectionActions: RowSelectionAction[] = [
    {
      label: 'Move wheel sets',
      onClick: () => setIdentifiersToMove(Object.keys(rowSelection).filter((id) => rowSelection[id]))
    },
    ...labelActions.rowSelectionActions
  ];

  const actions: TableAction<StorageObjectGroupRecordListV2>[] = [
    {
      label: 'Move wheel set',
      actionType: 'swap',
      onClick: (row) => {
        setIdentifiersToMove([row.identifier]);
      }
    },
    ...labelActions.rowActions,
    copyLicensePlateAction((row: StorageObjectGroupRecordListV2) => row.carWheelSet?.licensePlate)
  ];

  return (
    <>
      <NoddiTable
        actions={actions}
        showRowMenuWithSelection
        title='Wheel sets'
        description='Select wheel sets to move them or print their labels.'
        data={storageObjectGroups?.results}
        setPagination={setPagination}
        pagination={pagination}
        totalCount={storageObjectGroups?.count}
        isPending={isStorageObjectGroupsPending}
        getUrlPath={
          isPartnerApp
            ? (row) => ({ url: partnerRoutes.storedItemDetail.getPath({ identifier: row.identifier }) })
            : (row) => ({
                url: getExternalAppUrl({
                  externalApp: AppType.Partner,
                  route: partnerRoutes.storedItemDetail.getPath({ identifier: row.identifier })
                }),
                isExternalUrl: true
              })
        }
        getRowId={(row) => row.identifier}
        columns={wheelSetColumns}
        isFetching={isStorageObjectGroupsFetching}
        pageSizeOptions={[10, 15, 25, 50, 100, 250, 500, 1000]}
        filters={filters}
        customNode={
          <div className='flex flex-wrap items-center gap-2'>
            <NoddiButton
              type='button'
              variant='outline'
              size='small'
              startIcon='Download'
              loading={isExportPending}
              disabled={(storageObjectGroups?.count ?? 0) === 0}
              onClick={() => void downloadWheelSetsExcel(buildFilterInput(licensePlateNumbers))}
            >
              Export
            </NoddiButton>
            <NoddiButton
              type='button'
              variant='outline'
              size='small'
              className='relative shrink-0'
              onClick={() => setIsLicensePlateDialogOpen(true)}
            >
              <span className='flex items-center gap-2'>
                Filter by license plates
                {licensePlateNumbers.length > 0 && (
                  <NoddiBadge
                    variant='outline'
                    className='h-5 border-none bg-gray-200 px-1.5 py-0 text-[10px] font-medium'
                  >
                    {licensePlateNumbers.length}
                  </NoddiBadge>
                )}
              </span>
            </NoddiButton>
          </div>
        }
        rowSelection={rowSelection}
        setRowSelection={(update) => {
          // Any selection the table makes is of the rows on the current page.
          labelActions.rememberSelectableRows(storageObjectGroups?.results ?? []);
          setRowSelection(update);
        }}
        rowSelectionActions={rowSelectionActions}
        rowSelectionSelectAllMatching={{
          onSelectAllMatching: () => void handleSelectAllMatchingWheelSets(),
          isLoading: isSelectAllMatchingPending,
          selectAllMatchingLabel: (totalMatchingCount) => `Select all ${totalMatchingCount} matching wheel sets`
        }}
      />

      {isLicensePlateDialogOpen && (
        <LicensePlateBulkFilterDialog
          onClose={() => setIsLicensePlateDialogOpen(false)}
          appliedLicensePlateNumbers={licensePlateNumbers}
          setLicensePlateNumbers={setLicensePlateNumbers}
          pagination={pagination}
          setPagination={setPagination}
          buildFilterInput={buildFilterInput}
          onApplied={() => setRowSelection({})}
          filters={filters}
        />
      )}

      <MoveStorageObjectsDialog
        defaultStorageFacilityId={defaultStorageFacilityId}
        isDialogOpen={identifiersToMove != null}
        setIsDialogOpen={(open) => {
          if (!open) {
            setIdentifiersToMove(null);
          }
        }}
        storageObjectIdentifiers={identifiersToMove ?? []}
        onSuccess={handleSuccess}
        {...(serviceDepartmentId
          ? { serviceDepartmentId, serviceOrganizationId: undefined }
          : { serviceOrganizationId: serviceOrganizationId!, serviceDepartmentId: undefined })}
      />

      {labelActions.labelPrint}
    </>
  );
};

const LICENSE_PLATES_URL_KEY = filterUrlState.urlKey('licensePlateNumbers');

/**
 * The plates the license plate dialog applies. Its control is the dialog, not the sidebar, so the table gets only its
 * param.
 */
const licensePlatesFilter = storageObjectGroupFilterUnit.compose(
  'license-plates',
  { [LICENSE_PLATES_URL_KEY]: filterUrlState.selection((value) => value.trim() || undefined) },
  (values, setValues) => {
    const plates = filterUrlState.includedValues<string>(
      { include: values[LICENSE_PLATES_URL_KEY] as string[] | null | undefined, exclude: [] },
      []
    );
    const write = (next: string[] | undefined) => setValues(() => ({ [LICENSE_PLATES_URL_KEY]: next }));
    return {
      controls: {
        id: 'licensePlateNumbers',
        type: 'multi-select',
        title: 'License plates',
        options: plates.map((plate) => ({ value: plate, label: plate })),
        canExclude: false,
        value: { include: plates, exclude: [] },
        onChange: (next) => write(next.include.length > 0 ? next.include.map(String) : undefined),
        reset: () => write(undefined),
        isInitial: values[LICENSE_PLATES_URL_KEY] === undefined
      },
      params: { licensePlateNumbers: plates }
    };
  },
  ['licensePlateNumbers']
);
